import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const TableCompletionQuestion = ({
  question,
  result,
  currentAnswer = '',
  submittedFirstTime,
  showExplanation,
  onAnswerChange,
  onToggleExplanation,
  canRetry = false
}) => {
  const isCorrect = result?.isCorrect;
  const isIncorrect = submittedFirstTime && !isCorrect;
  const showResult = submittedFirstTime;

  // Determine if input should be disabled
  const isDisabled = submittedFirstTime && (!canRetry || isCorrect);

  // Get border color based on result
  const getBorderColor = () => {
    if (!showResult) return 'border-gray-300 focus:border-blue-500';
    if (isCorrect) return 'border-green-500';
    if (isIncorrect) return 'border-red-500';
    return 'border-gray-300';
  };

  // Get background color based on result
  const getBackgroundColor = () => {
    if (!showResult) return 'bg-white';
    if (isCorrect) return 'bg-green-50';
    if (isIncorrect && !canRetry) return 'bg-red-50';
    return 'bg-white';
  };

  const handleInputChange = (e) => {
    if (!isDisabled) {
      onAnswerChange(e.target.value);
    }
  };

  return (
    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
      {/* Question Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="font-semibold text-gray-800">Question {question.id}</span>
            {showResult && (
              <>
                {isCorrect ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
              </>
            )}
          </div>

          <h4 className="font-semibold text-gray-800 mb-2">
            Question {question.id}: Table Completion
          </h4>

          <p className="text-gray-700 mb-3">
            {question.question}
          </p>

          {/* Instructions */}
          {question.instructions && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
              {/* Instructions*/}
              <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-sm">
                {/* Left side - Image */}
                <div className="flex-shrink-0">
                  <img
                    src="/AI_Video/test2_scenario1_reading_task1.jpg"
                    alt="Reading Task Illustration"
                    className="w-48 h-56 object-cover rounded-lg shadow-md border-2 border-white"
                  />
                </div>

                {/* Right side - Instructions */}
                <div className="flex-1">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <div className="text-blue-800 leading-relaxed space-y-2">
                      <p className="font-medium">
                        After comparing the jobs, you gather more information. You find eight short reviews written by former senior employees from the two English centers.
                      </p>
                      <p>
                        <strong>First, read the reviews from 4 past senior teaching assistants of ABC centre (job 1).</strong> Can you recognize what aspects of the job they review? Read then check your understanding by answering questions 8-11 below.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="mb-4">
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-400">
            {/* Headers */}
            {question.table?.headers && (
              <thead className="bg-gray-100">
                <tr>
                  {question.table.headers.map((header, index) => (
                    <th
                      key={index}
                      className="border border-gray-400 px-4 py-3 text-left font-semibold text-gray-800"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
            )}

            {/* Rows */}
            <tbody>
              {question.table?.rows?.map((row, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {Object.entries(row).map(([key, value], cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border border-gray-400 px-4 py-3 text-gray-700"
                    >
                      {/* Check if this cell contains the blank to fill */}
                      {value && value.includes('______') ? (
                        <div className="flex items-center">
                          <span className="mr-2">
                            {value.split('______')[0]}
                          </span>
                          <div className="flex-1 max-w-xs">
                            <input
                              type="text"
                              value={currentAnswer}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              placeholder="Enter your answer"
                              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getBorderColor()} ${getBackgroundColor()} ${isDisabled ? 'cursor-not-allowed' : ''
                                }`}
                            />
                          </div>
                          <span className="ml-2">
                            {value.split('______')[1] || ''}
                          </span>
                        </div>
                      ) : (
                        value
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Answer key if provided */}
        {question.key && (
          <div className="mt-3 text-sm text-gray-600">
            <strong>Key:</strong> {question.key}
          </div>
        )}
      </div>

      {/* Result Display */}
      {showResult && (
        <div className="mb-4">
          <div className={`p-3 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center space-x-2">
              {isCorrect ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={`font-medium ${isCorrect ? 'text-green-800' : 'text-red-800'}`}>
                {isCorrect ? 'Correct!' : 'Incorrect'}
              </span>
            </div>

            <div className="mt-2 text-sm">
              <div className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                <strong>Your answer:</strong> {result?.selected || 'No answer provided'}
              </div>
              {!isCorrect && (
                <div className="text-gray-700 mt-1">
                  <strong>Correct answer:</strong> {question.answer}
                </div>
              )}
            </div>

            {/* Score information */}
            {result?.attempts && (
              <div className="mt-2 text-sm text-gray-600">
                <strong>Score:</strong> {result.score} point{result.score !== 1 ? 's' : ''}
                {result.attempts > 1 && ` (${result.attempts} attempts)`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Explanation Toggle */}
      {showResult && question.feedback && (
        <div className="mt-4">
          <button
            onClick={onToggleExplanation}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showExplanation ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {showExplanation ? 'Hide Explanation' : 'Show Explanation'}
            </span>
          </button>

          {/* Explanation Content */}
          {showExplanation && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <strong>Explanation:</strong>
                  <div className="mt-1">
                    {isCorrect
                      ? question.feedback.correct
                      : question.feedback.incorrect || question.feedback.incorrect?.general
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Retry hint */}
      {canRetry && isIncorrect && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              You can modify your answer and try again.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableCompletionQuestion;